import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import BootstrapTable from 'react-bootstrap-table-next';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { useTranslation } from 'react-i18next';
import { getUsersByIdAPI } from "../../api/identity";
import { actionCreators } from '../../store/Notifications';
import Card from '../Cards/Card';
import { ExpandableContent } from '../CustomControls/ExpandableContent';
import { ProtocolPicker } from '../CustomControls/ProtocolPicker';
import { Readed, Unread, View } from '../Icons/Icons';
import { generateLink } from '../Utils/Commons';
import { dateFormatter } from '../Utils/Dates';
import { Tooltip } from '../Utils/Tooltips';

const UserNotificationsCode = ({ notifications, requestUnreadNotifications, requestMarkAsReadedNotification }) => {
    const { t } = useTranslation();

    const [visitNotifications, setVisitNotifications] = useState([]);
    const [monitorNotifications, setMonitorNotifications] = useState([]);
    const [validateFileNotifications, setValidateFileNotifications] = useState([]);
    const [otherNotifications, setOtherNotifications] = useState([]);

    useEffect(() => {
        requestUnreadNotifications();
    }, []);

    useEffect(() => {
        const notificationsArray = Array.isArray(notifications) ? notifications : (notifications?.items ?? notifications?.data ?? []);
        if (notificationsArray.length === 0) return;

        //obtener usuarios para luego fijarse con el createdBy de quien viene la notificacion
        const userIds = notificationsArray.map(n => n.createdBy);
        const uniqueUserIds = [...new Set(userIds)];

        const fetchUsers = async () => {
            try {
                const res = await getUsersByIdAPI(uniqueUserIds);

                if (!res || !res.data) {
                    console.log("Sin notificaciones")
                    return;
                }
                const userMap = {};
                res.data.forEach(usuario => {
                    userMap[usuario.userId] = usuario.fullName || usuario.userId;
                });


                //dividir en diferentes estados las notificaciones
                const visits = [];
                const monitors = [];
                const validateFiles = [];
                const others = [];

                notificationsArray.forEach(n => {
                    const notificationWithCreatedByName = {
                        ...n,
                        createdByName: userMap[n.createdBy] || n.createdBy
                    };

                    const type = n.contentType ?? 'OTROS';

                    if (type === 1) { // Nota de Visita
                        visits.push(notificationWithCreatedByName);
                    } else if (type === 2) { // Nota de Monitor
                        monitors.push(notificationWithCreatedByName);
                    } else if (type === 3) { // Upload de Archivo
                        validateFiles.push(notificationWithCreatedByName);
                    } else { // Otras
                        others.push(n);
                    }
                });

                setVisitNotifications(visits);
                setMonitorNotifications(monitors);
                setValidateFileNotifications(validateFiles);
                setOtherNotifications(others);

            } catch (err) {
                console.error("Error al obtener usuarios:", err);
            }
        }

        if (uniqueUserIds)
            fetchUsers();

    }, [notifications]);

    return (
        <Card
            title={t("notifications.notifications")}
            className="card-notifications"
            content={
                <div className="card-notifications">
                    <ExpandableContent title={`${t("notifications.monitorNotes")} (${monitorNotifications.length || 0})`}>
                        <NotificationsCode
                            notifications={monitorNotifications}
                            requestMarkAsReadedNotification={requestMarkAsReadedNotification}
                            titulo={t("notifications.monitorNotes")}
                        />
                    </ExpandableContent>

                    <ExpandableContent title={`${t("notifications.visitNotes")} (${visitNotifications.length || 0})`}>
                        <NotificationsCode
                            notifications={visitNotifications}
                            requestMarkAsReadedNotification={requestMarkAsReadedNotification}
                        />
                    </ExpandableContent>

                    <ExpandableContent title={`${t("notifications.fileValidation")} (${validateFileNotifications.length || 0})`}>
                        <NotificationsCode
                            notifications={validateFileNotifications}
                            requestMarkAsReadedNotification={requestMarkAsReadedNotification}
                        />
                    </ExpandableContent>

                    <ExpandableContent title={`${t("notifications.others")} (${otherNotifications.length || 0})`}>
                        <NotificationsCode
                            notifications={otherNotifications}
                            requestMarkAsReadedNotification={requestMarkAsReadedNotification}
                        />
                    </ExpandableContent>
                </div>
            }
        />
    );
}

const UsersNotifications = connect(
    state => state.notifications,
    dispatch => bindActionCreators(actionCreators, dispatch)
)(UserNotificationsCode);

export default UsersNotifications;

const NotificationsCode = ({ notifications, requestMarkAsReadedNotification }) => {
    const { t } = useTranslation();

    const [paged, setPaged] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalSize, setTotalSize] = useState(0);
    const [query, setQuery] = useState({
        pagination: { page: 1, sizePerPage: 10, totalSizeMonitor: 0 },
        sort: { sortField: null, sortOrder: null },
        filters: { protocolId: null }
    });

    useEffect(() => {
        setIsLoading(true);
        let filtered = [...notifications];

        const { sortField, sortOrder } = query.sort;

        if (sortField && sortOrder) {
            filtered.sort((a, b) => {
                const aValue = a[sortField];
                const bValue = b[sortField];
                if (aValue == null) return 1;
                if (bValue == null) return -1;

                if (typeof aValue === 'string') {
                    return sortOrder === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                } else {
                    return sortOrder === 'asc'
                        ? aValue - bValue
                        : bValue - aValue;
                }
            });
        }

        if (query.filters.protocolId) {
            filtered = filtered.filter(n => {
                const params = n.params || "";
                const protocolId = new URLSearchParams(params).get("protocolId");
                return protocolId && Number(protocolId) === query.filters.protocolId;
            });
        }

        const { page, sizePerPage } = query.pagination;
        const startIndex = (page - 1) * sizePerPage;
        const endIndex = startIndex + sizePerPage;
        const paged = filtered.slice(startIndex, endIndex);

        setTotalSize(filtered.length);
        setPaged(paged)
        setIsLoading(false)

    }, [query, notifications]);

    const columns = [
        { dataField: 'created', text: t("notifications.date"), formatter: function (cell, row) { return (<div>{dateFormatter(cell, "DD/MMM/YYYY")} {row.lockTo}</div>) } },
        { dataField: 'createdByName', text: t("notifications.user") },
        { dataField: 'content', text: t("notifications.content") },
        {
            dataField: 'view',
            text: '',
            isDummyField: true,
            formatter: (_cell, row, rowIndex) => (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <NavLink to={generateLink(row.params)} activeClassName="active">
                        <View style={{ fontSize: '20px' }} />
                    </NavLink>

                    {row.status === 0 ? (
                        <Tooltip tooltip={t("notifications.markAsRead")} id={`tt-${rowIndex}`}>
                            <button
                                className="btn-simple btn-table-actions"
                                type="button"
                                onClick={() => requestMarkAsReadedNotification(row.id)}
                            >
                                <Unread />
                            </button>
                        </Tooltip>
                    ) : (
                        <Tooltip tooltip={t("notifications.Read")} id={`tt-${rowIndex}`}>
                            <div className="btn-simple btn-table-actions">
                                <Readed />
                            </div>
                        </Tooltip>
                    )}
                </div>
            ),
        },
    ];

    const handleFilterChange = (name, selectedValues) => {
        const newQuery = { ...query };
        newQuery.filters.protocolId = selectedValues?.length ? selectedValues[0].id : null;
        newQuery.pagination.page = 1;
        setQuery(newQuery);
    }

    const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {

        var newQuery = JSON.parse(JSON.stringify(query));
        switch (type) {
            case 'pagination': {
                newQuery.pagination.page = page;
                newQuery.pagination.sizePerPage = sizePerPage;
                setQuery(newQuery);
                break;
            }
            case 'sort': {
                newQuery.sort.sortField = sortField;
                newQuery.sort.sortOrder = sortOrder;
                setQuery(newQuery);
                break;
            }
            default: break;
        }
    }

    return (
        <>
            <div className="row">
                <div className="col-md-4">
                    <ProtocolPicker
                        id="agenda-protocol-picker"
                        name="protocols"
                        multiple={true}
                        placeholder={t("filters.protocol")}
                        onChange={handleFilterChange}
                    />
                </div>
            </div>
            <BootstrapTable
                classes="table-spaced table-spaced-small"
                wrapperClasses="table-responsive"
                bordered={false}
                hover
                remote
                keyField='id'
                data={paged}
                columns={columns}
                noDataIndication={() => <>{t("commons.noData")}</>}
                loading={isLoading}
                overlay={overlayFactory({ spinner: true })}
                pagination={paginationFactory({
                    page: query.pagination.page,
                    sizePerPage: query.pagination.sizePerPage,
                    totalSize: totalSize,
                    hideSizePerPage: true,
                })}
                onTableChange={handleTableChange}
            />
        </>

    )

}